import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink } from 'react-router-dom';
import { Form, Field } from 'react-final-form';
import { InputAdornment, Link, Stack, Typography } from '@mui/material';
import { Email } from '@mui/icons-material';
import PrimaryLoadingButton from 'components/shared/buttons/PrimaryLoadingButton';
import PasswordFieldAdapter from 'components/shared/field-adapters/PasswordFieldAdapter';
import TextFieldAdapter from 'components/shared/field-adapters/TextFieldAdapter';
import GoogleAuthButton from 'components/auth/portal/GoogleAuthButton';
import requestStatus from 'common/constants/status';
import routes from 'common/routes';
import StyledNavigationLink from 'common/theme/links/StyledNavigationLink';
import validateLoginForm from 'common/validations/form/login';
import authSlice from 'store/modules/auth';

const loginInitialValues = {
  username: '',
  password: ''
};

/**
 * Formulario de INGRESO (extraído de la vieja pantalla Login): mismos campos,
 * validación y submit. El card/hero/logo los pone AuthCard; la redirección
 * post-login vive en AuthPortal.
 */
function LoginForm() {
  const dispatch = useDispatch();
  const { t } = useTranslation(['buttons', 'labels', 'common']);

  const passwordReset = useSelector((state) => state.auth.passwordReset);
  const status = useSelector((state) => state.auth.status);

  useEffect(() => {
    if (passwordReset) {
      dispatch(authSlice.actions.resetPasswordReset());
    }
  }, [dispatch, passwordReset]);

  const onLogin = (values) => {
    dispatch(authSlice.actions.login(values));
  };

  return (
    <Form
      initialValues={loginInitialValues}
      validate={validateLoginForm}
      onSubmit={onLogin}
      render={({ handleSubmit }) => (
        <form onSubmit={handleSubmit}>
          <Stack spacing={2}>
            <Stack spacing={3}>
              <Stack spacing={2} sx={{ width: '100%' }}>
                <Field
                  id="email"
                  name="username"
                  component={TextFieldAdapter}
                  autoComplete="email"
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="start">
                        <Email />
                      </InputAdornment>
                    )
                  }}
                  label={t('email', { ns: 'labels' })}
                  type="text"
                  fullWidth
                  required
                />
                <Field
                  id="password"
                  name="password"
                  component={PasswordFieldAdapter}
                  label={t('password', { ns: 'labels' })}
                  autoComplete="current-password"
                  fullWidth
                  required
                />
              </Stack>
              <PrimaryLoadingButton
                disabled={status === requestStatus.loading}
                loading={status === requestStatus.loading}
                label={t('auth.login', { ns: 'buttons' })}
                type="submit"
              />
              <GoogleAuthButton />
            </Stack>
            <StyledNavigationLink
              component={RouterLink}
              to={routes.forgotPassword.path}
              variant="subtitle2"
              underline="always">
              {t('auth.forgotPassword', { ns: 'buttons' })}
            </StyledNavigationLink>
            <Typography variant="subtitle2" align="center" sx={{ color: 'text.secondary' }}>
              {t('auth.noAccount', { ns: 'common', defaultValue: '¿No tienes cuenta?' })}{' '}
              <Link component={RouterLink} to={routes.register.path} underline="always" sx={{ color: 'secondary.main' }}>
                {t('auth.createAccount', { ns: 'common', defaultValue: 'Crear cuenta' })}
              </Link>
            </Typography>
          </Stack>
        </form>
      )}
    />
  );
}

export default LoginForm;
